import React from 'react';
import styled from '@emotion/styled';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { useNavigate, useLocation } from 'react-router-dom';
import slugify from '@sindresorhus/slugify';
import parseQS from 'helpers/parseQS';

import MainLayout from 'layouts/MainLayout';
import Header from 'components/Header';
import Footer from 'components/Footer';
import Helmet from 'components/Helmet';
import LinearProgress from 'components/LinearProgress';
import BgLeft from 'assets/icons/download5.svg?react';
import BgRight from 'assets/icons/download6.svg?react';
import TrollGreetings from 'assets/images/careerTest/troll-greetings.png';
import InstitutionsListEndpoint from 'endPoints/institutionsList';
import useDataListFactory from 'services/dataList';
import SortIcon from 'assets/icons/iconoir_sort.svg?react';
import PinIcon from 'assets/icons/location.svg?react';
import ArrowContainedIcon from 'assets/icons/arrow_contained.svg?react';
import FiltersIcon from 'assets/icons/filters.svg?react';
import CheckboxIcon from 'assets/icons/checkbox.svg?react';
import SearchToggleInput from 'components/SearchToggleInput';
import { ResultsWrapper, ResultsControls } from 'components/ResultsLayout';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import { getLanguage } from 'translation';
import CardGridSkeleton from 'components/CardGridSkeleton';

const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));

const useDataList = (props, options) => useDataListFactory(props, options);

// Helper function for Ukrainian pluralization of "програм"
const getProgramsWord = (count) => {
  const num = Math.abs(count) % 100;
  const lastDigit = num % 10;
  
  if (num > 10 && num < 20) {
    return 'програм'; // 11-19
  }
  if (lastDigit === 1) {
    return 'програма'; // 1, 21, 31, etc.
  }
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'програми'; // 2, 3, 4, 22, 23, 24, etc.
  }
  return 'програм'; // 5, 6, 7, 8, 9, 10, 20, 25, etc.
};

const Background = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.tertiary.pale,
  position: 'relative',
  minHeight: '100vh',
  fontFamily: 'Rubik-Regular'
}));

const BgLeftStyled = styled(BgLeft)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const BgRightStyled = styled(BgRight)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  bottom: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TrollGreetingsStyled = styled('img')(({ theme }) => ({
  position: 'absolute',
  bottom: -100,
  right: 0,
  zIndex: 10,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TrollGreetingsStyledTop = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: 70,
  right: -120,
  transform: 'rotate(-45deg)',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const Content = styled('div')(({ theme }) => ({
  ...theme.wrapper,
  maxWidth: 1240,
  paddingTop: 30,
  paddingBottom: 56,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  [theme.breakpoints.down('lg')]: {
    paddingLeft: 20,
    paddingRight: 20
  }
}));

const Card = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: 18,
  border: '1px solid rgba(10, 10, 10, 1)',
  boxShadow: '4px 4px 0px 0px rgba(10, 10, 10, 1)',
  padding: 24,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  marginBottom: 24
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 48,
  fontWeight: 500,
  fontFamily: 'Unbounded',
  lineHeight: '56px',
  marginBottom: 24,
  color: theme.palette.textPrimary.main,
  [theme.breakpoints.down('sm')]: {
    fontSize: 32,
    lineHeight: '40px'
  }
}));

const List = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 24,
  marginBottom: 8,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(1, 1fr)',
    gap: 16
  }
}));

const InstitutionCard = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: 18,
  border: '1px solid rgba(10, 10, 10, 1)',
  boxShadow: '4px 4px 0px 0px rgba(10, 10, 10, 1)',
  padding: 24,
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)'
  }
}));

const InstitutionName = styled(Typography)(() => ({
  fontSize: 18,
  fontWeight: 500,
  fontFamily: 'Rubik-Regular',
  marginBottom: 8,
  color: 'rgba(10, 10, 10, 1)'
}));

const InstitutionShortName = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontFamily: 'Rubik-Regular',
  color: theme.palette.textTertiary?.main || 'rgba(100, 100, 100, 1)',
  marginBottom: 12
}));

const InstitutionAddress = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
  fontFamily: 'Rubik-Regular',
  color: theme.palette.textTertiary?.main || 'rgba(100, 100, 100, 1)',
  '& svg': {
    width: 16,
    height: 16,
    flexShrink: 0
  }
}));

const InstitutionFooter = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: 16
}));

const ProgramsCount = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.primary.main,
  fontWeight: 500
}));

const ViewButton = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32
}));

const ArrowIconStyled = styled(ArrowContainedIcon)(() => ({
  width: 32,
  height: 32
}));

const BreadcrumbsWrapper = styled('div')(() => ({
  marginBottom: 24
}));

const ResultsLabel = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontFamily: 'Rubik-Regular',
  paddingLeft: 3,
  color: theme.palette.textDarkGray?.main || 'rgba(100, 100, 100, 1)'
}));

const ResultsLabelContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8
}));

const IconButtonStyled = styled(IconButton)(() => ({
  padding: 0,
  margin: 3
}));

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 24,
  [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' }
}));

const LeftSidebar = styled('div')(() => ({ marginBottom: 20, flex: 2 }));

const RightSidebar = styled('div')(({ theme }) => ({
  flex: 1,
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    paddingRight: 0
  }
}));

const Filters = styled('div')(() => ({
  borderRadius: 18,
  backgroundColor: 'rgba(121, 201, 18, 0.1)',
  padding: 24,
  paddingTop: 16,
  paddingBottom: 16
}));

const FiltersTitle = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  fontWeight: 500,
  fontFamily: 'Rubik-Regular',
  marginBottom: 12,
  display: 'flex',
  alignItems: 'center',
  '& svg': { marginRight: 16 },
  [theme.breakpoints.down('sm')]: {
    cursor: 'pointer',
    marginBottom: 0
  }
}));

const FiltersHeadline = styled(Typography)(() => ({
  fontSize: 16,
  fontWeight: 500,
  fontFamily: 'Rubik-Regular',
  marginBottom: 0,
  marginTop: 16,
  marginLeft: -2
}));

const CheckboxStyled = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.primary.main,
  padding: 3,
  marginLeft: 8,
  width: 21,
  height: 21,
  '& svg': {
    width: 21,
    height: 21
  }
}));

const FormGroupStyled = styled(FormGroup)(() => ({ 
  marginBottom: 0,
  marginTop: 0,
  '& + &': {
    marginTop: -4
  }
}));

const FormControlLabelStyled = styled(FormControlLabel)(() => ({
  fontSize: 14,
  fontFamily: 'Rubik-Regular',
  marginBottom: 0,
  marginTop: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: 14,
    paddingTop: 2
  }
}));

const FilterWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: { marginTop: 24 }
}));

const SEOSection = styled('div')(({ theme }) => ({
  marginTop: 48,
  paddingTop: 32,
  borderTop: '1px solid rgba(238, 237, 242, 1)',
  [theme.breakpoints.down('sm')]: {
    marginTop: 32,
    paddingTop: 24
  }
}));

const SEOTitle = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 600,
  fontFamily: 'Unbounded',
  marginBottom: 16,
  color: theme.palette.textPrimary.main,
  [theme.breakpoints.down('sm')]: {
    fontSize: 20,
    marginBottom: 12
  }
}));

const SEOParagraph = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 400,
  fontFamily: 'Rubik-Regular',
  lineHeight: '24px',
  color: theme.palette.textPrimary.main,
  marginBottom: 16,
  [theme.breakpoints.down('sm')]: {
    fontSize: 14,
    lineHeight: '22px',
    marginBottom: 12
  }
}));

const InstitutionsPage = ({ institutionType }) => {
  const helmet = useTranslate('HELMET');
  const t = useTranslate('EducationPrograms');
  const tUni = useTranslate('University');
  const isMobile = useSelector((state) => state.app.isMobile);
  const navigate = useNavigate();
  const location = useLocation();
  const lang = getLanguage().toUpperCase();
  
  // Read initial filters and sort from URL
  const initialFilters = React.useMemo(() => {
    const urlParams = parseQS(location.search);
    const initialFiltersObj = institutionType ? { institutionType } : {};
    
    if (urlParams.filters) {
      if (urlParams.filters.locationType) {
        initialFiltersObj.locationType = urlParams.filters.locationType;
      }
      if (urlParams.filters.financingType) {
        initialFiltersObj.financingType = Array.isArray(urlParams.filters.financingType)
          ? urlParams.filters.financingType
          : [urlParams.filters.financingType];
      }
      if (urlParams.filters.studyOpportunities) {
        initialFiltersObj.studyOpportunities = Array.isArray(urlParams.filters.studyOpportunities)
          ? urlParams.filters.studyOpportunities
          : [urlParams.filters.studyOpportunities];
      }
      if (urlParams.filters.search) {
        initialFiltersObj.search = urlParams.filters.search;
      }
    }
    
    // If no location filter is specified in URL, don't apply any location filtering
    // This ensures all institutions are shown by default
    if (!initialFiltersObj.locationType) {
      initialFiltersObj.ignore_location = 'true';
    }
    
    return initialFiltersObj;
  }, [location.search, institutionType]);

  const initialSort = React.useMemo(() => {
    const urlParams = parseQS(location.search);
    if (urlParams.sort) {
      return urlParams.sort;
    }
    return { programsCount: 'desc' };
  }, [location.search]);

  const dataListKey = `institutionsList_${institutionType || 'all'}`;

  const { actions, data, loading, page, perPage, count, sort, filters } = useDataList(
    {
      ...InstitutionsListEndpoint,
      sourceName: dataListKey
    },
    {
      filters: initialFilters,
      sort: initialSort
    }
  );

  const [query, setQuery] = React.useState('');
  const [sortAnchorEl, setSortAnchorEl] = React.useState(null);
  const sortMenuOpen = Boolean(sortAnchorEl);
  const [filtersView, setFiltersView] = React.useState(true);

  // Update URL when filters or sort change
  const updateURLWithFilters = React.useCallback((newFilters, newSort) => {
    const urlParams = new URLSearchParams(location.search);
    
    // Remove existing filter params
    Array.from(urlParams.keys()).forEach(key => {
      if (key.startsWith('filters[') || key.startsWith('sort[')) {
        urlParams.delete(key);
      }
    });
    
    // Add new filter params
    if (newFilters) {
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            // Only add array if it has items
            if (value.length > 0) {
              value.forEach((v) => {
                if (v !== undefined && v !== null && v !== '') {
                  urlParams.append(`filters[${key}]`, v);
                }
              });
            }
          } else {
            urlParams.set(`filters[${key}]`, String(value));
          }
        }
      });
    }
    
    // Add new sort params
    if (newSort) {
      Object.entries(newSort).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          urlParams.set(`sort[${key}]`, String(value));
        }
      });
    }
    
    const newSearch = urlParams.toString();
    navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const handleOpenChange = React.useCallback(
    (isOpen) => {
      if (!isOpen) {
        setQuery('');
        actions.onSearchChange('');
        const nextFilters = { ...filters };
        delete nextFilters.search;
        updateURLWithFilters(nextFilters, sort);
      }
    },
    [actions, filters, sort, updateURLWithFilters]
  );

  const handleChange = React.useCallback((e) => {
    setQuery(e.target.value);
  }, []);

  const handleSearch = React.useCallback(() => {
    actions.onSearchChange(query);
    const nextFilters = { ...filters, search: query };
    updateURLWithFilters(nextFilters, sort);
  }, [actions, query, filters, sort, updateURLWithFilters]);

  const handleChangePage = React.useCallback(async () => {
    actions.onChangePage(page, true, true);
  }, [actions, page]);

  const handleSort = React.useCallback(
    (column, direction) => {
      let newSort;
      if (sort[column] && sort[column] === direction) {
        newSort = { ...sort };
        delete newSort[column];
        actions.onSortChange(column, null);
      } else {
        newSort = { ...sort, [column]: direction };
        actions.onSortChange(column, direction);
      }
      setSortAnchorEl(null);
      updateURLWithFilters(filters, newSort);
    },
    [actions, sort, filters, updateURLWithFilters]
  );

  const sortValues = React.useMemo(
    () => [
      { column: 'name', direction: 'asc', name: t('ByNameAsc') },
      { column: 'name', direction: 'desc', name: t('ByNameDesc') },
      { column: 'programsCount', direction: 'desc', name: t('ByProgramsCountDesc') }
    ],
    [t]
  );

  const limitReached = React.useMemo(() => count <= page * perPage, [count, page, perPage]);

  const { sentinelRef } = useInfiniteScroll({
    isLoading: loading,
    hasMore: !limitReached,
    onLoadMore: handleChangePage,
    rootMargin: '200px 0px',
    threshold: 0
  });

  const handleInstitutionClick = React.useCallback(
    (institution) => {
      const name = institution.name || institution[`name${lang}`] || 'institution';
      const slug = slugify(name);
      const path = institutionType === 'college' ? 'colleges' : 'universities';
      navigate(`/education/${path}/${slug}/${institution._id}`);
    },
    [navigate, institutionType, lang]
  );

  const pageTitle = React.useMemo(() => {
    if (institutionType === 'college') return t('TitleCollegesInstitutions');
    return t('TitleUniversitiesInstitutions');
  }, [institutionType, t]);

  const breadcrumbs = React.useMemo(() => {
    const items = [
      { link: '/', label: 'Home' },
      { link: '/education', label: 'EducationProgramsPage' }
    ];

    // Use raw property to pass already-translated text
    items.push({ 
      link: institutionType === 'college' ? '/education/colleges' : '/education/universities', 
      label: pageTitle,
      raw: true
    });

    return items;
  }, [institutionType, pageTitle]);

  const handleToggleFiltersView = React.useCallback(() => {
    if (!isMobile) return;
    setFiltersView((prev) => !prev);
  }, [isMobile]);

  const handleChangeFilter = React.useCallback(
    (name, value) => {
      let nextFilters;
      if (typeof value === 'boolean') {
        nextFilters = { ...filters };
        if (filters[name] === value) {
          delete nextFilters[name];
        } else {
          nextFilters[name] = value;
        }
        // Clean filters before sending
        const cleanedFilters = Object.fromEntries(
          Object.entries(nextFilters).filter(([_, v]) => 
            v !== undefined && v !== null && v !== '' && 
            !(Array.isArray(v) && v.length === 0)
          )
        );
        actions.onFilterChange(cleanedFilters, true);
        updateURLWithFilters(cleanedFilters, sort);
        return;
      }
      
      // Handle array filters (financingType, studyOpportunities)
      // Convert string to array if needed
      let source = [];
      if (Array.isArray(filters[name])) {
        source = filters[name];
      } else if (typeof filters[name] === 'string' && filters[name]) {
        source = filters[name].split(',').filter(Boolean);
      }
      
      const result = source.includes(value)
        ? source.filter((v) => v !== value)
        : [...source, value];
      
      if (result.filter(Boolean).length > 0) {
        nextFilters = {
          ...filters,
          [name]: result.filter(Boolean)
        };
      } else {
        // Remove the filter if empty
        nextFilters = { ...filters };
        delete nextFilters[name];
      }
      
      // Clean filters before sending - remove empty arrays and undefined values
      const cleanedFilters = Object.fromEntries(
        Object.entries(nextFilters).filter(([_, v]) => 
          v !== undefined && v !== null && v !== '' && 
          !(Array.isArray(v) && v.length === 0)
        )
      );
      
      actions.onFilterChange(cleanedFilters, true);
      updateURLWithFilters(cleanedFilters, sort);
    },
    [actions, filters, sort, updateURLWithFilters]
  );

  const handleLocationTypeChange = React.useCallback(
    (locationType) => {
      const nextFilters = { ...filters };
      // Remove ignore_location when user selects a specific location type
      delete nextFilters.ignore_location;
      
      if (filters.locationType === locationType) {
        // If clicking the same option, remove the filter (show all institutions)
        delete nextFilters.locationType;
        nextFilters.ignore_location = 'true';
      } else {
        // Set the new location type
        nextFilters.locationType = locationType;
      }
      actions.onFilterChange(nextFilters, true);
      updateURLWithFilters(nextFilters, sort);
    },
    [filters, actions, sort, updateURLWithFilters]
  );

  const CheckBoxItem = React.useMemo(
    () =>
      React.memo(({ item, checked, onChange }) => (
        <FormGroupStyled>
          <FormControlLabelStyled
            control={
              <CheckboxStyled checked={checked} onChange={onChange} icon={<CheckboxIcon />} />
            }
            label={item}
          />
        </FormGroupStyled>
      )),
    []
  );

  return (
    <>
      <Helmet
        meta={{
          title:
            institutionType === 'college'
              ? helmet('collegesTitle')
              : helmet('universitiesTitle'),
          description:
            institutionType === 'college'
              ? helmet('collegesDescription')
              : helmet('universitiesDescription'),
          ogTitle:
            institutionType === 'college'
              ? helmet('collegesOgTitle')
              : helmet('universitiesOgTitle'),
          ogDescription:
            institutionType === 'college'
              ? helmet('collegesOgDescription')
              : helmet('universitiesOgDescription')
        }}
      />
      <MainLayout>
        <Header />
        <Background>
          <Content>
            <React.Suspense fallback={null}>
              <BreadcrumbsWrapper>
                <Breadcrumbs breadcrumbs={breadcrumbs} />
              </BreadcrumbsWrapper>
            </React.Suspense>
            <Card>
              <Title variant="h1">{pageTitle}</Title>

              <Wrapper>
                <LeftSidebar>
                  <ResultsWrapper>
                {data && data.length === 0 ? (
                  <ResultsLabel>{t('NoData')}</ResultsLabel>
                ) : (
                  <>
                    <ResultsLabelContainer>
                      <ResultsLabel>{t('FoundResultsInstitutions', { count })}</ResultsLabel>
                      {loading && <CircularProgress size={16} />}
                    </ResultsLabelContainer>
                    <ResultsControls>
                      {!isMobile ? (
                        <SearchToggleInput
                          value={query}
                          onChange={handleChange}
                          onSearch={handleSearch}
                          onOpenChange={handleOpenChange}
                          placeholder={t('SearchPlaceholder')}
                          openAriaLabel={t('OpenSearch')}
                          closeAriaLabel={t('CloseSearch')}
                          searchAriaLabel={t('Search')}
                        />
                      ) : null}
                      <IconButtonStyled
                        aria-label={t('Sort')}
                        onClick={(e) => setSortAnchorEl(e.currentTarget)}>
                        <SortIcon />
                      </IconButtonStyled>
                    </ResultsControls>
                    <Menu
                      anchorEl={sortAnchorEl}
                      open={sortMenuOpen}
                      onClose={() => setSortAnchorEl(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                      {sortValues.map((item) => (
                        <MenuItem
                          key={`${item.column}-${item.direction}`}
                          selected={sort[item.column] === item.direction}
                          onClick={() => handleSort(item.column, item.direction)}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
                  </ResultsWrapper>

                  <List>
                {(data || []).map((institution, idx) => {
                  const name = institution.name || institution[`name${lang}`] || 'Institution';
                  const shortName =
                    institution.shortName || institution[`shortName${lang}`] || '';
                  const address = institution.contactInfo?.katottgName
                    ? `${institution.contactInfo.katottgName}${
                        institution.contactInfo?.address
                          ? `, ${institution.contactInfo.address}`
                          : ''
                      }`
                    : institution.contactInfo?.address || '';

                  return (
                    <InstitutionCard
                      key={institution._id || idx}
                      onClick={() => handleInstitutionClick(institution)}>
                      <InstitutionName>{name}</InstitutionName>
                      {shortName && <InstitutionShortName>{shortName}</InstitutionShortName>}
                      {address && (
                        <InstitutionAddress>
                          <PinIcon />
                          <span>{address}</span>
                        </InstitutionAddress>
                      )}
                      <InstitutionFooter>
                        <ProgramsCount>
                          {(() => {
                            const count = institution.programsCount || 0;
                            const lang = getLanguage();
                            if (lang === 'ua' || lang === 'uk') {
                              return `${count} ${getProgramsWord(count)}`;
                            }
                            return `${count} ${tUni('programs')}`;
                          })()}
                        </ProgramsCount>
                        <ViewButton>
                          <ArrowIconStyled />
                        </ViewButton>
                      </InstitutionFooter>
                    </InstitutionCard>
                  );
                })}
              </List>

                  {!data && loading ? <CardGridSkeleton count={6} /> : null}

            {loading || !data ? <LinearProgress /> : null}
                  {limitReached ? null : (
                    <div ref={sentinelRef} aria-label={t('LoadMore')} style={{ height: 1 }} />
                  )}

                  <SEOSection>
                    <SEOTitle variant="h3">
                      {institutionType === 'college' 
                        ? 'Коледжі та училища в Україні' 
                        : 'Вищі навчальні заклади в Україні'}
                    </SEOTitle>
                    <SEOParagraph>
                      {institutionType === 'college' 
                        ? 'Коледжі та училища в Україні пропонують широкий спектр освітніх програм для отримання професійної освіти. Ці навчальні заклади готують кваліфікованих спеціалістів різних галузей, надаючи практичні навички та теоретичні знання, необхідні для успішної кар\'єри. Сучасні коледжі та училища обладнані сучасними лабораторіями, майстернями та навчальними аудиторіями, що забезпечують якісну підготовку студентів відповідно до вимог ринку праці.'
                        : 'Вищі навчальні заклади в Україні є основою системи вищої освіти та готують висококваліфікованих спеціалістів для різних сфер економіки. Університети та інститути пропонують бакалаврські, магістерські та докторські програми, що відповідають міжнародним стандартам освіти. Сучасні вищі навчальні заклади активно розвивають міжнародне співробітництво, беруть участь у програмі обміну студентами та реалізують спільні освітні проекти з провідними університетами світу.'}
                    </SEOParagraph>
                    <SEOParagraph>
                      {institutionType === 'college' 
                        ? 'Вибір коледжу або училища є важливим кроком у житті кожного абітурієнта, оскільки саме тут формується професійна база та навички, які визначатимуть майбутню кар\'єру. Багато коледжів та училищ мають тісні зв\'язки з підприємствами та організаціями, що дозволяє студентам проходити практику та отримувати роботу після закінчення навчання. Крім того, навчальні заклади постійно оновлюють освітні програми, враховуючи зміни на ринку праці та вимоги роботодавців.'
                        : 'Освітні програми вищих навчальних закладів охоплюють широкий спектр спеціальностей: від технічних та природничих наук до гуманітарних та соціальних дисциплін. Кожен університет та інститут має свої унікальні традиції, наукові школи та дослідницькі центри, що робить навчальний процес різноманітним та цікавим. Студенти мають можливість брати участь у наукових дослідженнях, конференціях та конкурсах, що сприяє розвитку критичного мислення та творчих здібностей.'}
                    </SEOParagraph>
                    <SEOParagraph>
                      {institutionType === 'college' 
                        ? 'Система освіти в коледжах та училищах України постійно вдосконалюється, інтегруючи сучасні технології та інноваційні методики навчання. Багато навчальних закладів пропонують дистанційне та заочне навчання, що робить освіту доступною для різних категорій студентів. Кваліфіковані викладачі, сучасна матеріально-технічна база та активне студентське життя створюють оптимальні умови для отримання якісної професійної освіти та всебічного розвитку особистості.'
                        : 'Вступ до вищого навчального закладу в Україні відкриває перед студентами численні можливості для професійного та особистісного зростання. Університети та інститути надають доступ до бібліотек, наукових баз даних, спортивних комплексів та культурних центрів, що сприяє всебічному розвитку. Випускники вищих навчальних закладів мають високі шанси на успішне працевлаштування та кар\'єрний ріст, оскільки отримують не лише фундаментальні знання, але й практичні навички, необхідні для роботи в сучасних умовах.'}
                    </SEOParagraph>
                  </SEOSection>
                </LeftSidebar>

                <RightSidebar>
                  <Filters>
                    <FiltersTitle onClick={handleToggleFiltersView}>
                      <FiltersIcon />
                      {t('filtersTitle')}
                    </FiltersTitle>

                    {filtersView ? (
                      <FilterWrapper>
                        {/* Location type filter */}
                        <FiltersHeadline>{t('LocationHeadline')}</FiltersHeadline>
                        <CheckBoxItem
                          item={t('LocationTypeLocal')}
                          checked={filters.locationType === 'local'}
                          onChange={() => handleLocationTypeChange('local')}
                        />
                        <CheckBoxItem
                          item={t('LocationTypeNational')}
                          checked={filters.locationType === 'national'}
                          onChange={() => handleLocationTypeChange('national')}
                        />
                        <CheckBoxItem
                          item={t('LocationTypeInternational')}
                          checked={filters.locationType === 'international'}
                          onChange={() => handleLocationTypeChange('international')}
                        />

                        {/* Financing Type filter */}
                        <FiltersHeadline>{t('InstitutionTypeHeadline')}</FiltersHeadline>
                        {(() => {
                          const financingType = Array.isArray(filters.financingType) 
                            ? filters.financingType 
                            : (typeof filters.financingType === 'string' && filters.financingType 
                              ? filters.financingType.split(',').filter(Boolean) 
                              : []);
                          return (
                            <>
                              <CheckBoxItem
                                item={t('InstitutionTypePublic')}
                                checked={financingType.includes('державний')}
                                onChange={() => handleChangeFilter('financingType', 'державний')}
                              />
                              <CheckBoxItem
                                item={t('InstitutionTypePrivate')}
                                checked={financingType.includes('приватний')}
                                onChange={() => handleChangeFilter('financingType', 'приватний')}
                              />
                              <CheckBoxItem
                                item={t('InstitutionTypeCommunal')}
                                checked={financingType.includes('комунальний')}
                                onChange={() => handleChangeFilter('financingType', 'комунальний')}
                              />
                            </>
                          );
                        })()}

                        {/* Study Opportunities filters */}
                        <FiltersHeadline>{t('ExtraParamsHeadline')}</FiltersHeadline>
                        {(() => {
                          const studyOpportunities = Array.isArray(filters.studyOpportunities) 
                            ? filters.studyOpportunities 
                            : (typeof filters.studyOpportunities === 'string' && filters.studyOpportunities 
                              ? filters.studyOpportunities.split(',').filter(Boolean) 
                              : []);
                          return (
                            <>
                              <CheckBoxItem
                                item={t('ExtraDormitory')}
                                checked={studyOpportunities.includes('dormitory')}
                                onChange={() => handleChangeFilter('studyOpportunities', 'dormitory')}
                              />
                              <CheckBoxItem
                                item={t('ExtraPostgraduate')}
                                checked={studyOpportunities.includes('postgraduateEducation')}
                                onChange={() => handleChangeFilter('studyOpportunities', 'postgraduateEducation')}
                              />
                              <CheckBoxItem
                                item={t('ExtraMilitaryDepartment')}
                                checked={studyOpportunities.includes('militaryDepartment')}
                                onChange={() => handleChangeFilter('studyOpportunities', 'militaryDepartment')}
                              />
                            </>
                          );
                        })()}
                      </FilterWrapper>
                    ) : null}
                  </Filters>
                </RightSidebar>
              </Wrapper>
            </Card>
            <TrollGreetingsStyled src={TrollGreetings} alt="" />
          </Content>
        </Background>
        <TrollGreetingsStyledTop src={TrollGreetings} alt="" />
        <BgLeftStyled />
        <BgRightStyled />
        <Footer />
      </MainLayout>
    </>
  );
};

export default InstitutionsPage;
